import React from "react";
import {
 Card,
 H1,
 H2,
 H3,
 H4,
 H5,
 Button,
 Icon,
 InputGroup,
 ControlGroup,
 FormGroup,
 NumericInput,
 Switch,
 NonIdealState,
 Spinner,
 Elevation,
 Text,
 Tag,
 Divider,
 Callout,
 ProgressBar,
 Classes,
 Dialog,
 Menu,
 MenuItem,
 Popover,
 Position,
} from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { DateInput } from "@blueprintjs/datetime";
import { inject, observer } from "mobx-react";
import { apiCallPost, apiCallGet } from "../../../micro/fns/util";
import cogoToast from "cogo-toast";
import _ from "lodash";
import moment from "moment";
import AsyncSelWrap from "../../../micro/utils/AsyncSelWrap";
import { BranchService } from "../../../micro/services/BranchService";
import SxPriceEstimator from "../../components/SxPriceEstimator";

class SurgeryWorkflow extends React.Component {
 state = {
  // Loading states
  isLoading: false,
  isSaving: false,

  // View mode: 'list' or 'create'
  viewMode: "list", // 'list' for surgery list, 'create' for workflow

  // Surgery list data
  surgeries: [],
  filteredSurgeries: [],
  filters: {
   status: "all", // all, scheduled, completed, cancelled
   dateFrom: null,
   dateTo: null,
   doctor: "",
   patient: "",
   surgeryType: "",
  },

  // Current step (only for create mode)
  currentStep: 1,

  // Patient data
  selectedPatient: null,
  patientSearchResults: [],
  isSearchingPatients: false,

  // Payment data
  paymentMethod: "cash", // cash, cghs, insurance, corporate
  selectedBranch: "",
  branches: [],

  // Doctor data
  doctors: [],
  selectedDoctor: null,
  doctorSearchResults: [],
  isSearchingDoctors: false,
  doctorSchedule: [],

  // Surgery data
  selectedSurgeryGroups: [],
  selectedSurgeries: [],
  surgeryGroups: [],
  availableSurgeries: [],

  // AI Analysis
  aiAnalysis: {
   patientHistory: "",
   riskFactors: [],
   recommendations: [],
   voiceNotes: "",
  },

  // Summary data
  surgeryDetails: {
   date: null,
   time: "",
   duration: 0,
   estimatedCost: 0,
   team: {
    surgeon: null,
    anesthetist: null,
    assistant: null,
   },
  },

  // Modals
  showSurgerySelectionModal: false,
  showDoctorScheduleModal: false,
 };

 componentDidMount = async () => {
  await this.loadInitialData();
 };

 loadInitialData = async () => {
  try {
   this.setState({ isLoading: true });

   // Load surgeries list
   await this.loadSurgeries();

   // Load branches, doctors, and surgery groups for create mode
   await Promise.all([
    this.loadBranches(),
    this.loadDoctors(),
    this.loadSurgeryGroups(),
   ]);
  } catch (error) {
   console.error("Error loading initial data:", error);
   cogoToast.error("Failed to load initial data");
  } finally {
   this.setState({ isLoading: false });
  }
 };

 loadSurgeries = async () => {
  try {
   const response = await apiCallGet(
    `/v3/${this.props.authStore.prefix}/surgeries`
   );
   const surgeries = response || [];
   this.setState({
    surgeries,
    filteredSurgeries: surgeries,
   });
  } catch (error) {
   console.error("Error loading surgeries:", error);
  }
 };

 loadBranches = async () => {
  try {
   const branches = await BranchService.getAllBranches(
    this.props.authStore.prefix
   );
   this.setState({ branches: branches || [] });
  } catch (error) {
   console.error("Error loading branches:", error);
   cogoToast.error("Failed to load branches");
  }
 };

 loadSurgeryGroups = async () => {
  try {
   const response = await apiCallGet(
    `/v3/${this.props.authStore.prefix}/surgeryGroups`
   );
   this.setState({ surgeryGroups: response || [] });
  } catch (error) {
   console.error("Error loading surgery groups:", error);
  }
 };

 // Step 1: Patient Search - Same as SurgeryBooking
 searchPatients = async (string) => {
  if (!string) {
   this.setState({ patientSearchResults: [] });
   return;
  }
  try {
   this.setState({ isSearchingPatients: true });
   let res = await apiCallPost(`/rai/${this.props.authStore.prefix}/search`, {
    prefix: string,
   });
   this.setState({
    patientSearchResults: res.map((each) => {
     return {
      item: _.startCase(_.lowerCase(each.name)),
      entityTypeString: each.genericPid,
      helper: each.phone,
      helper2: new Date().getFullYear() - new Date(each.dob).getFullYear(),
      patientData: each,
     };
    }),
    isSearchingPatients: false,
   });
  } catch (err) {
   cogoToast.error(
    "There was an error in searching. Please check the console for more details."
   );
   console.log(err);
   this.setState({ isSearchingPatients: false });
  }
 };

 selectPatient = async (item) => {
  let obj;
  try {
   let res = await apiCallPost(
    `/clinical2/${this.props.authStore.prefix}/extra/getPatientInfo`,
    {
     patientId: item.patientData.genericPid,
    }
   );

   obj = {
    _id: res.sysPid,
    name: res.name,
    phone: res.phone,
    address: res.address,
    patientId: res.genericPid,
    age: new Date().getFullYear() - new Date(res.dob).getFullYear(),
    gender: res.sex,
    insuranceStatus: res.insuranceStatus || "None",
    allergies: res.allergies || [],
    medicalHistory: res.medicalHistory || [],
   };

   this.setState({ selectedPatient: obj });

   // Load patient details and AI analysis
   await this.loadPatientDetails(obj._id);

   // Move to next step
   this.nextStep();

   cogoToast.success(`Patient ${obj.name} selected`);
  } catch (error) {
   console.error("Error selecting patient:", error);
   cogoToast.error("Failed to select patient");
  }
 };

 loadPatientDetails = async (patientId) => {
  try {
   const response = await apiCallGet(
    `/clinical2/${this.props.authStore.prefix}/extra/getPatientInfo`,
    {
     patientId,
    }
   );

   if (response) {
    // Generate AI analysis based on patient data
    const aiAnalysis = this.generateAIAnalysis(response);
    this.setState({ aiAnalysis });
   }
  } catch (error) {
   console.error("Error loading patient details:", error);
  }
 };

 generateAIAnalysis = (patientData) => {
  // Simulate AI analysis based on patient data
  const analysis = {
   patientHistory: `Primary Condition: ${
    patientData.primaryCondition || "Bilateral Cataract with secondary glaucoma"
   }\nPrevious Surgeries: ${
    patientData.previousSurgeries || "None"
   }\nCurrent Medications: ${
    patientData.currentMedications || "Timolol eye drops, Latanoprost"
   }\nAllergies: ${patientData.allergies || "Penicillin (moderate reaction)"}`,
   riskFactors: patientData.riskFactors || ["Diabetes Type 2", "Hypertension"],
   recommendations: [
    "Dr. Priya Mehta has excellent experience with complex cataract cases",
    "Local anesthesia is recommended for this patient",
    "Consider pre-operative consultation with cardiologist",
   ],
   voiceNotes: "",
  };

  return analysis;
 };

 // Step 2: Payment Panel
 handlePaymentMethodChange = (method) => {
  this.setState({ paymentMethod: method });
 };

 handleBranchChange = (branch) => {
  this.setState({ selectedBranch: branch });
 };

 // Step 3: Doctor Search - Same as DoctorScheduleMaster
 loadDoctors = async () => {
  try {
   const res = await apiCallPost(
    `/rai/${this.props.authStore.prefix}/getAllConsultants`,
    {}
   );
   this.setState({
    doctors: _.sortBy(res, "name").map((doc) => ({
     ...doc,
     searchName: `${doc.shortcode || ""} | ${doc.name}`,
    })),
   });
  } catch (err) {
   console.error("Failed to load doctors:", err);
   // Fallback to dummy doctors
   this.setState({
    doctors: [
     { _id: "1", name: "Dr. Ammar", shortcode: "AMM" },
     { _id: "2", name: "Dr. Smith", shortcode: "SMT" },
     { _id: "3", name: "Dr. Johnson", shortcode: "JOH" },
    ],
   });
  }
 };

 searchDoctors = async (query) => {
  if (!query || query.length < 2) return [];

  try {
   this.setState({ isSearchingDoctors: true });

   // Filter from loaded doctors
   const filteredDoctors = this.state.doctors.filter(
    (doctor) =>
     doctor.name.toLowerCase().includes(query.toLowerCase()) ||
     doctor.shortcode?.toLowerCase().includes(query.toLowerCase()) ||
     doctor.searchName.toLowerCase().includes(query.toLowerCase())
   );

   // Format for AsyncSelWrap - same as patient search
   const doctors = filteredDoctors.map((doctor) => ({
    item: doctor.name,
    entityTypeString: doctor.shortcode || "",
    helper: doctor.specialization || "Ophthalmology",
    helper2: doctor.experience || "5+ years",
    doctorData: doctor,
   }));

   this.setState({ doctorSearchResults: doctors });
   return doctors;
  } catch (error) {
   console.error("Error searching doctors:", error);
   cogoToast.error("Failed to search doctors");
   return [];
  } finally {
   this.setState({ isSearchingDoctors: false });
  }
 };

 selectDoctor = async (item) => {
  try {
   const doctor = item.doctorData;
   this.setState({ selectedDoctor: doctor });

   // Load doctor schedule
   await this.loadDoctorSchedule(doctor._id);

   cogoToast.success(`Dr. ${doctor.name} selected`);
  } catch (error) {
   console.error("Error selecting doctor:", error);
   cogoToast.error("Failed to select doctor");
  }
 };

 loadDoctorSchedule = async (doctorId) => {
  try {
   const response = await apiCallGet(
    `/v3/${this.props.authStore.prefix}/doctors/${doctorId}/schedule`
   );
   this.setState({ doctorSchedule: response || [] });
  } catch (error) {
   console.error("Error loading doctor schedule:", error);
  }
 };

 // Step 4: Surgery Selection
 handleSurgeryGroupToggle = (groupId) => {
  const { selectedSurgeryGroups } = this.state;
  const newGroups = selectedSurgeryGroups.includes(groupId)
   ? selectedSurgeryGroups.filter((id) => id !== groupId)
   : [...selectedSurgeryGroups, groupId];

  this.setState({ selectedSurgeryGroups: newGroups });

  // Load surgeries for selected groups
  this.loadSurgeriesForGroups(newGroups);
 };

 loadSurgeriesForGroups = async (groupIds) => {
  try {
   const response = await apiCallPost(
    `/v3/${this.props.authStore.prefix}/surgeries/byGroups`,
    {
     groupIds,
    }
   );
   this.setState({ availableSurgeries: response || [] });
  } catch (error) {
   console.error("Error loading surgeries:", error);
  }
 };

 // Filter methods
 applyFilters = () => {
  const { surgeries, filters } = this.state;
  let filtered = [...surgeries];

  if (filters.status !== "all") {
   filtered = filtered.filter((surgery) => surgery.status === filters.status);
  }

  if (filters.dateFrom) {
   filtered = filtered.filter((surgery) =>
    moment(surgery.scheduledDate).isSameOrAfter(filters.dateFrom, "day")
   );
  }

  if (filters.dateTo) {
   filtered = filtered.filter((surgery) =>
    moment(surgery.scheduledDate).isSameOrBefore(filters.dateTo, "day")
   );
  }

  if (filters.doctor) {
   filtered = filtered.filter((surgery) =>
    surgery.doctorName?.toLowerCase().includes(filters.doctor.toLowerCase())
   );
  }

  if (filters.patient) {
   filtered = filtered.filter((surgery) =>
    surgery.patientName?.toLowerCase().includes(filters.patient.toLowerCase())
   );
  }

  if (filters.surgeryType) {
   filtered = filtered.filter((surgery) =>
    surgery.surgeryType
     ?.toLowerCase()
     .includes(filters.surgeryType.toLowerCase())
   );
  }

  this.setState({ filteredSurgeries: filtered });
 };

 handleFilterChange = (filterName, value) => {
  this.setState(
   (prevState) => ({
    filters: {
     ...prevState.filters,
     [filterName]: value,
    },
   }),
   this.applyFilters
  );
 };

 clearFilters = () => {
  this.setState(
   {
    filters: {
     status: "all",
     dateFrom: null,
     dateTo: null,
     doctor: "",
     patient: "",
     surgeryType: "",
    },
   },
   () => {
    this.setState({ filteredSurgeries: this.state.surgeries });
   }
  );
 };

 handleSurgeryToggle = (surgeryId) => {
  const { selectedSurgeries } = this.state;
  const newSurgeries = selectedSurgeries.includes(surgeryId)
   ? selectedSurgeries.filter((id) => id !== surgeryId)
   : [...selectedSurgeries, surgeryId];

  this.setState({ selectedSurgeries: newSurgeries });
 };

 // Step 5: Final Scheduling
 handleScheduleSurgery = async () => {
  try {
   this.setState({ isSaving: true });

   const surgeryData = {
    patientId: this.state.selectedPatient.id,
    doctorId: this.state.selectedDoctor.id,
    surgeries: this.state.selectedSurgeries,
    paymentMethod: this.state.paymentMethod,
    branch: this.state.selectedBranch,
    scheduledDate: this.state.surgeryDetails.date,
    scheduledTime: this.state.surgeryDetails.time,
    estimatedCost: this.state.surgeryDetails.estimatedCost,
   };

   const response = await apiCallPost(
    `/v3/${this.props.authStore.prefix}/surgeries/schedule`,
    surgeryData
   );

   cogoToast.success("Surgery scheduled successfully!");

   // Reset form or redirect
   this.resetWorkflow();
  } catch (error) {
   console.error("Error scheduling surgery:", error);
   cogoToast.error("Failed to schedule surgery");
  } finally {
   this.setState({ isSaving: false });
  }
 };

 // Navigation
 nextStep = () => {
  this.setState((prevState) => ({
   currentStep: Math.min(prevState.currentStep + 1, 5),
  }));
 };

 prevStep = () => {
  this.setState((prevState) => ({
   currentStep: Math.max(prevState.currentStep - 1, 1),
  }));
 };

 resetWorkflow = () => {
  this.setState({
   currentStep: 1,
   selectedPatient: null,
   selectedDoctor: null,
   selectedSurgeryGroups: [],
   selectedSurgeries: [],
   paymentMethod: "cash",
   selectedBranch: "",
   surgeryDetails: {
    date: null,
    time: "",
    duration: 0,
    estimatedCost: 0,
    team: {
     surgeon: null,
     anesthetist: null,
     assistant: null,
    },
   },
  });
 };

 // Mode switching
 switchToCreateMode = () => {
  this.setState({
   viewMode: "create",
   currentStep: 1,
   selectedPatient: null,
   selectedDoctor: null,
   selectedSurgeryGroups: [],
   selectedSurgeries: [],
  });
 };

 switchToListMode = () => {
  this.setState({
   viewMode: "list",
   currentStep: 1,
   selectedPatient: null,
   selectedDoctor: null,
   selectedSurgeryGroups: [],
   selectedSurgeries: [],
  });
  // Reload surgeries list
  this.loadSurgeries();
 };

 // Render methods
 renderSurgeryList = () => (
  <div>
   {/* Header with Create New Button */}
   <div
    style={{
     display: "flex",
     justifyContent: "space-between",
     alignItems: "center",
     marginBottom: "24px",
    }}
   >
    <H1 style={{ color: "var(--text-primary)", margin: 0 }}>
     Surgery Scheduling
    </H1>
    <div style={{ display: "flex", gap: "12px" }}>
      <SxPriceEstimator />
      <Button
        intent="primary"
        icon={<Icon icon={IconNames.PLUS} />}
        text="Create New Surgery"
        onClick={this.switchToCreateMode}
        large
      />
    </div>
   </div>

   {/* Filters */}
   <Card
    elevation={Elevation.TWO}
    className="bp3-dark"
    style={{ marginBottom: "24px" }}
   >
    <H3 style={{ color: "var(--text-primary)", marginBottom: "16px" }}>
     <Icon icon={IconNames.FILTER} style={{ marginRight: "8px" }} />
     Filters
    </H3>

    <div
     style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
      gap: "16px",
      marginBottom: "16px",
     }}
    >
     <FormGroup label="Status" labelFor="status-filter">
      <select
       value={this.state.filters.status}
       onChange={(e) => this.handleFilterChange("status", e.target.value)}
       style={{
        backgroundColor: "var(--bg-surface)",
        color: "var(--text-primary)",
        border: "1px solid var(--border-color)",
        borderRadius: "4px",
        padding: "8px 12px",
        width: "100%",
       }}
      >
       <option value="all">All Status</option>
       <option value="scheduled">Scheduled</option>
       <option value="completed">Completed</option>
       <option value="cancelled">Cancelled</option>
      </select>
     </FormGroup>

     <FormGroup label="From Date" labelFor="date-from">
      <DateInput
       value={this.state.filters.dateFrom}
       onChange={(date) => this.handleFilterChange("dateFrom", date)}
       placeholder="Select start date"
       formatDate={(date) => moment(date).format("YYYY-MM-DD")}
       parseDate={(str) => moment(str, "YYYY-MM-DD").toDate()}
       fill
      />
     </FormGroup>

     <FormGroup label="To Date" labelFor="date-to">
      <DateInput
       value={this.state.filters.dateTo}
       onChange={(date) => this.handleFilterChange("dateTo", date)}
       placeholder="Select end date"
       formatDate={(date) => moment(date).format("YYYY-MM-DD")}
       parseDate={(str) => moment(str, "YYYY-MM-DD").toDate()}
       fill
      />
     </FormGroup>

     <FormGroup label="Doctor" labelFor="doctor-filter">
      <InputGroup
       value={this.state.filters.doctor}
       onChange={(e) => this.handleFilterChange("doctor", e.target.value)}
       placeholder="Search doctor"
       leftIcon={<Icon icon={IconNames.USER} />}
       fill
      />
     </FormGroup>

     <FormGroup label="Patient" labelFor="patient-filter">
      <InputGroup
       value={this.state.filters.patient}
       onChange={(e) => this.handleFilterChange("patient", e.target.value)}
       placeholder="Search patient"
       leftIcon={<Icon icon={IconNames.PERSON} />}
       fill
      />
     </FormGroup>

     <FormGroup label="Surgery Type" labelFor="surgery-type-filter">
      <InputGroup
       value={this.state.filters.surgeryType}
       onChange={(e) => this.handleFilterChange("surgeryType", e.target.value)}
       placeholder="Search surgery type"
       leftIcon={<Icon icon={IconNames.MEDICAL_CROSS} />}
       fill
      />
     </FormGroup>
    </div>

    <div style={{ display: "flex", gap: "8px" }}>
     <Button
      text="Clear Filters"
      icon={<Icon icon={IconNames.CROSS} />}
      onClick={this.clearFilters}
      minimal
     />
     <Button
      text="Apply Filters"
      icon={<Icon icon={IconNames.FILTER} />}
      onClick={this.applyFilters}
      intent="primary"
      minimal
     />
    </div>
   </Card>

   {/* Surgery List */}
   <Card elevation={Elevation.TWO} className="bp3-dark">
    <H3 style={{ color: "var(--text-primary)", marginBottom: "16px" }}>
     <Icon icon={IconNames.CALENDAR} style={{ marginRight: "8px" }} />
     Scheduled Surgeries ({this.state.filteredSurgeries.length})
    </H3>

    {this.state.filteredSurgeries.length === 0 ? (
     <NonIdealState
      icon={<Icon icon={IconNames.CALENDAR} size={48} />}
      title="No surgeries found"
      description="No surgeries match your current filters. Try adjusting your search criteria."
     />
    ) : (
     <div
      style={{
       display: "grid",
       gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))",
       gap: "16px",
      }}
     >
      {this.state.filteredSurgeries.map((surgery) => (
       <Card
        key={surgery._id}
        elevation={Elevation.ONE}
        interactive
        style={{
         backgroundColor: "var(--bg-surface)",
         border: "1px solid var(--border-color)",
         cursor: "pointer",
        }}
       >
        <div style={{ padding: "16px" }}>
         <div
          style={{
           display: "flex",
           justifyContent: "space-between",
           alignItems: "flex-start",
           marginBottom: "12px",
          }}
         >
          <H5 style={{ color: "var(--text-primary)", margin: 0 }}>
           {surgery.patientName}
          </H5>
          <Tag
           intent={
            surgery.status === "completed"
             ? "success"
             : surgery.status === "cancelled"
             ? "danger"
             : "primary"
           }
           minimal
          >
           {surgery.status?.toUpperCase()}
          </Tag>
         </div>

         <div style={{ marginBottom: "8px" }}>
          <Text style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
           <Icon
            icon={IconNames.USER}
            size={12}
            style={{ marginRight: "4px" }}
           />
           Dr. {surgery.doctorName}
          </Text>
         </div>

         <div style={{ marginBottom: "8px" }}>
          <Text style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
           <Icon
            icon={IconNames.CALENDAR}
            size={12}
            style={{ marginRight: "4px" }}
           />
           {moment(surgery.scheduledDate).format("MMM DD, YYYY")} at{" "}
           {surgery.scheduledTime}
          </Text>
         </div>

         <div style={{ marginBottom: "8px" }}>
          <Text style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
           <Icon
            icon={IconNames.MEDICAL_CROSS}
            size={12}
            style={{ marginRight: "4px" }}
           />
           {surgery.surgeryType}
          </Text>
         </div>

         <div
          style={{
           display: "flex",
           justifyContent: "space-between",
           alignItems: "center",
           marginTop: "12px",
           paddingTop: "12px",
           borderTop: "1px solid var(--border-color)",
          }}
         >
          <Text
           style={{
            color: "var(--primary)",
            fontSize: "16px",
            fontWeight: "bold",
           }}
          >
           ₹{surgery.estimatedCost?.toLocaleString() || "0"}
          </Text>
          <div style={{ display: "flex", gap: "8px" }}>
           <Button
            icon={<Icon icon={IconNames.EDIT} />}
            minimal
            small
            onClick={(e) => {
             e.stopPropagation();
             // Handle edit
            }}
           />
           <Button
            icon={<Icon icon={IconNames.EYE_OPEN} />}
            minimal
            small
            onClick={(e) => {
             e.stopPropagation();
             // Handle view details
            }}
           />
          </div>
         </div>
        </div>
       </Card>
      ))}
     </div>
    )}
   </Card>
  </div>
 );

 renderPatientSearch = () => (
  <Card elevation={Elevation.TWO} className="bp3-dark">
   <H3 style={{ color: "var(--text-primary)", marginBottom: "16px" }}>
    <Icon icon={IconNames.PERSON} style={{ marginRight: "8px" }} />
    Patient Search
   </H3>

   <FormGroup label="Search Patients" className="v3-form-group">
    <AsyncSelWrap
     disabled={false}
     autoFocus={true}
     placeholder="Type patient name, phone, or ID..."
     searching={this.state.isSearchingPatients}
     items={_.take(this.state.patientSearchResults, 10)}
     selectedItem={null}
     update={(item) => this.searchPatients(item)}
     onChange={async (item) => {
      await this.selectPatient(item);
     }}
    />
   </FormGroup>

   {this.state.selectedPatient && (
    <div
     style={{
      marginTop: "16px",
      padding: "12px",
      backgroundColor: "var(--bg-surface)",
      borderRadius: "4px",
     }}
    >
     <H5 style={{ color: "var(--text-primary)", margin: "0 0 8px 0" }}>
      {this.state.selectedPatient.name}
     </H5>
     <Text style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
      Patient ID: {this.state.selectedPatient.patientId} | Age:{" "}
      {this.state.selectedPatient.age} years | Gender:{" "}
      {this.state.selectedPatient.gender}
     </Text>
     <br />
     <Text style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
      Phone: {this.state.selectedPatient.phone} | Insurance:{" "}
      {this.state.selectedPatient.insuranceStatus}
     </Text>
    </div>
   )}
  </Card>
 );

 renderPaymentPanel = () => (
  <Card elevation={Elevation.TWO} className="bp3-dark">
   <H3 style={{ color: "var(--text-primary)", marginBottom: "16px" }}>
    <Icon icon={IconNames.CREDIT_CARD} style={{ marginRight: "8px" }} />
    Payment Panel
   </H3>

   <div
    style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}
   >
    <FormGroup label="Payment Method" labelFor="payment-method">
     <ControlGroup>
      {["cash", "cghs", "insurance", "corporate"].map((method) => (
       <Button
        key={method}
        text={method.toUpperCase()}
        intent={this.state.paymentMethod === method ? "primary" : "none"}
        onClick={() => this.handlePaymentMethodChange(method)}
        style={{ textTransform: "uppercase" }}
       />
      ))}
     </ControlGroup>
    </FormGroup>

    <FormGroup label="Branch" labelFor="branch-select">
     <ControlGroup>
      <select
       value={this.state.selectedBranch}
       onChange={(e) => this.handleBranchChange(e.target.value)}
       style={{
        backgroundColor: "var(--bg-surface)",
        color: "var(--text-primary)",
        border: "1px solid var(--border-color)",
        borderRadius: "4px",
        padding: "8px 12px",
        minWidth: "200px",
       }}
      >
       <option value="">Select Branch</option>
       {this.state.branches.map((branch, index) => {
        const branchName =
         branch.site ||
         branch.name ||
         branch.branchName ||
         branch.siteName ||
         branch;
        return (
         <option key={branch._id || index} value={branchName}>
          {branchName}
         </option>
        );
       })}
      </select>
     </ControlGroup>
    </FormGroup>
   </div>
  </Card>
 );

 renderDoctorSearch = () => (
  <Card elevation={Elevation.TWO} className="bp3-dark">
   <H3 style={{ color: "var(--text-primary)", marginBottom: "16px" }}>
    <Icon icon={IconNames.USER} style={{ marginRight: "8px" }} />
    Doctor Selection
   </H3>

   <FormGroup label="Search Doctors" className="v3-form-group">
    <AsyncSelWrap
     disabled={false}
     autoFocus={true}
     placeholder="Type doctor name or specialization..."
     searching={this.state.isSearchingDoctors}
     items={_.take(this.state.doctorSearchResults, 10)}
     selectedItem={null}
     update={(item) => this.searchDoctors(item)}
     onChange={async (item) => {
      await this.selectDoctor(item);
     }}
    />
   </FormGroup>

   {this.state.selectedDoctor && (
    <div
     style={{
      marginTop: "16px",
      padding: "12px",
      backgroundColor: "var(--bg-surface)",
      borderRadius: "4px",
     }}
    >
     <H5 style={{ color: "var(--text-primary)", margin: "0 0 8px 0" }}>
      Dr. {this.state.selectedDoctor.name}
     </H5>
     <Text style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
      {this.state.selectedDoctor.specialization} | Experience:{" "}
      {this.state.selectedDoctor.experience} | Rating:{" "}
      {this.state.selectedDoctor.rating}/5
     </Text>
    </div>
   )}
  </Card>
 );

 renderSurgerySelection = () => (
  <Card elevation={Elevation.TWO} className="bp3-dark">
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
      <H3 style={{ color: "var(--text-primary)", margin: 0 }}>
        <Icon icon={IconNames.MEDICAL_CROSS} style={{ marginRight: "8px" }} />
        Surgery Selection
      </H3>
      <SxPriceEstimator />
    </div>

   <div style={{ marginBottom: "20px" }}>
    <H4 style={{ color: "var(--text-primary)", marginBottom: "12px" }}>
     Surgery Groups
    </H4>
    <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
     {this.state.surgeryGroups.map((group) => (
      <Button
       key={group._id}
       text={group.name}
       intent={
        this.state.selectedSurgeryGroups.includes(group._id)
         ? "primary"
         : "none"
       }
       onClick={() => this.handleSurgeryGroupToggle(group._id)}
       style={{ marginBottom: "8px" }}
      />
     ))}
    </div>
   </div>

   {this.state.availableSurgeries.length > 0 && (
    <div>
     <H4 style={{ color: "var(--text-primary)", marginBottom: "12px" }}>
      Available Surgeries
     </H4>
     <div
      style={{
       display: "grid",
       gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
       gap: "12px",
      }}
     >
      {this.state.availableSurgeries.map((surgery) => (
       <Card
        key={surgery._id}
        elevation={Elevation.ONE}
        interactive
        onClick={() => this.handleSurgeryToggle(surgery._id)}
        style={{
         backgroundColor: this.state.selectedSurgeries.includes(surgery._id)
          ? "var(--primary)"
          : "var(--bg-surface)",
         cursor: "pointer",
         border: this.state.selectedSurgeries.includes(surgery._id)
          ? "2px solid var(--primary)"
          : "1px solid var(--border-color)",
        }}
       >
        <H5 style={{ color: "var(--text-primary)", margin: "0 0 8px 0" }}>
         {surgery.name}
        </H5>
        <Text style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
         {surgery.description}
        </Text>
        <div style={{ marginTop: "8px" }}>
         <Tag intent="success" minimal>
          ₹{surgery.estimatedCost}
         </Tag>
         <Tag intent="warning" minimal style={{ marginLeft: "8px" }}>
          {surgery.duration} min
         </Tag>
        </div>
       </Card>
      ))}
     </div>
    </div>
   )}
  </Card>
 );

 renderAIAnalysis = () => (
  <Card
   elevation={Elevation.TWO}
   className="bp3-dark"
   style={{ marginBottom: "20px" }}
  >
   <H3 style={{ color: "var(--text-primary)", marginBottom: "16px" }}>
    <Icon icon={IconNames.AUTOMATIC_UPDATES} style={{ marginRight: "8px" }} />
    AI Patient Analysis
   </H3>

   <div style={{ marginBottom: "16px" }}>
    <Button
     intent="warning"
     icon={<Icon icon={IconNames.MICROPHONE} />}
     text="Start Voice Notes"
     onClick={() => cogoToast.info("Voice notes feature coming soon")}
    />
   </div>

   <div style={{ marginBottom: "16px" }}>
    <H4 style={{ color: "var(--text-primary)", marginBottom: "8px" }}>
     Patient History Summary (AI-powered)
    </H4>
    <Text style={{ color: "var(--text-secondary)", whiteSpace: "pre-line" }}>
     {this.state.aiAnalysis.patientHistory}
    </Text>
   </div>

   <div style={{ marginBottom: "16px" }}>
    <H4 style={{ color: "var(--text-primary)", marginBottom: "8px" }}>
     Risk Factors
    </H4>
    <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
     {this.state.aiAnalysis.riskFactors.map((risk, index) => (
      <Tag key={index} intent="danger" minimal>
       {risk}
      </Tag>
     ))}
    </div>
   </div>

   <div>
    <H4 style={{ color: "var(--text-primary)", marginBottom: "8px" }}>
     AI Recommendations
    </H4>
    {this.state.aiAnalysis.recommendations.map((rec, index) => (
     <Callout key={index} intent="primary" style={{ marginBottom: "8px" }}>
      {rec}
     </Callout>
    ))}
   </div>
  </Card>
 );

 renderSurgeryDetailsSummary = () => (
  <Card elevation={Elevation.TWO} className="bp3-dark">
   <H3 style={{ color: "var(--text-primary)", marginBottom: "16px" }}>
    <Icon icon={IconNames.DOCUMENT} style={{ marginRight: "8px" }} />
    Surgery Details Summary
   </H3>

   <div style={{ marginBottom: "16px" }}>
    <Text style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
     Surgery Date & Time:{" "}
     {this.state.surgeryDetails.date
      ? moment(this.state.surgeryDetails.date).format("YYYY-MM-DD")
      : "Not selected"}{" "}
     at {this.state.surgeryDetails.time || "Not selected"}
    </Text>
   </div>

   <div style={{ marginBottom: "16px" }}>
    <Text style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
     Surgery Type:{" "}
     {this.state.selectedSurgeries.length > 0
      ? this.state.selectedSurgeries.length + " selected"
      : "None selected"}
    </Text>
   </div>

   <div style={{ marginBottom: "16px" }}>
    <Text style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
     Medical Team:{" "}
     {this.state.selectedDoctor
      ? `Dr. ${this.state.selectedDoctor.name}`
      : "Not selected"}
    </Text>
   </div>

   <div style={{ marginBottom: "16px" }}>
    <Text style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
     Payment: {this.state.paymentMethod.toUpperCase()} -{" "}
     {this.state.selectedBranch || "No branch selected"}
    </Text>
   </div>

   <Divider />

   <div style={{ marginTop: "16px" }}>
    <H4 style={{ color: "var(--text-primary)", marginBottom: "8px" }}>
     Estimated Cost
    </H4>
    <Text
     style={{ color: "var(--primary)", fontSize: "18px", fontWeight: "bold" }}
    >
     ₹{this.state.surgeryDetails.estimatedCost.toLocaleString()}
    </Text>
   </div>
  </Card>
 );

 renderStepIndicator = () => {
  const steps = [
   "Patient Search",
   "Payment Panel",
   "Doctor Selection",
   "Surgery Selection",
   "Schedule Confirmation",
  ];

  return (
   <div style={{ marginBottom: "24px" }}>
    <H2 style={{ color: "var(--text-primary)", marginBottom: "16px" }}>
     Surgery Scheduling Workflow
    </H2>
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
     {steps.map((step, index) => (
      <React.Fragment key={index}>
       <div
        style={{
         display: "flex",
         alignItems: "center",
         gap: "8px",
         padding: "8px 16px",
         backgroundColor:
          index + 1 <= this.state.currentStep
           ? "var(--primary)"
           : "var(--bg-surface)",
         color:
          index + 1 <= this.state.currentStep
           ? "white"
           : "var(--text-secondary)",
         borderRadius: "20px",
         fontSize: "14px",
         fontWeight: "500",
        }}
       >
        <Icon icon={IconNames.CIRCLE} size={12} />
        {step}
       </div>
       {index < steps.length - 1 && (
        <Icon
         icon={IconNames.CHEVRON_RIGHT}
         size={16}
         style={{ color: "var(--text-secondary)" }}
        />
       )}
      </React.Fragment>
     ))}
    </div>
   </div>
  );
 };

 renderCurrentStep = () => {
  switch (this.state.currentStep) {
   case 1:
    return this.renderPatientSearch();
   case 2:
    return this.renderPaymentPanel();
   case 3:
    return this.renderDoctorSearch();
   case 4:
    return this.renderSurgerySelection();
   case 5:
    return (
     <Card elevation={Elevation.TWO} className="bp3-dark">
      <H3 style={{ color: "var(--text-primary)", marginBottom: "16px" }}>
       <Icon icon={IconNames.CONFIRM} style={{ marginRight: "8px" }} />
       Schedule Confirmation
      </H3>
      <Text style={{ color: "var(--text-secondary)", marginBottom: "20px" }}>
       Please review all details before confirming the surgery schedule.
      </Text>
      <div style={{ display: "flex", gap: "12px" }}>
       <Button
        intent="primary"
        icon={<Icon icon={IconNames.CONFIRM} />}
        text="Confirm Schedule"
        onClick={this.handleScheduleSurgery}
        loading={this.state.isSaving}
       />
       <Button
        text="Edit Details"
        onClick={() => this.setState({ currentStep: 1 })}
       />
      </div>
     </Card>
    );
   default:
    return null;
  }
 };

 render() {
  if (this.state.isLoading) {
   return (
    <NonIdealState
     icon={<Spinner />}
     title="Loading Surgery Workflow"
     description="Please wait while we load the necessary data..."
    />
   );
  }

  // Show surgery list view
  if (this.state.viewMode === "list") {
   return (
    <div
     style={{
      padding: "20px",
      backgroundColor: "var(--bg-primary)",
      color: "var(--text-primary)",
      minHeight: "100vh",
     }}
     className="bp3-dark"
    >
     {this.renderSurgeryList()}
    </div>
   );
  }

  // Show create workflow view
  return (
   <div
    style={{
     padding: "20px",
     backgroundColor: "var(--bg-primary)",
     color: "var(--text-primary)",
     minHeight: "100vh",
    }}
    className="bp3-dark"
   >
    {/* Header */}
    <div style={{ marginBottom: "24px" }}>
     <div
      style={{
       display: "flex",
       justifyContent: "space-between",
       alignItems: "center",
      }}
     >
      {/* <div>
       <H1 style={{ color: "var(--text-primary)", margin: "0 0 8px 0" }}>
        Eye Hospital - Surgery Scheduling
       </H1>
       <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
        <Tag large intent="primary">
         Specialization: Ophthalmology
        </Tag>
        <Tag large intent="success">
         AI POWERED
        </Tag>
       </div>
      </div> */}
      <div style={{ display: "flex", alignItems: "center" }}>
        <SxPriceEstimator />
        <Button
         text="Back to List"
         icon={<Icon icon={IconNames.ARROW_LEFT} />}
         onClick={this.switchToListMode}
         outlined
         style={{
          borderColor: "var(--border-color)",
          color: "var(--text-primary)",
          marginLeft: "12px"
         }}
        />
      </div>
     </div>
    </div>

    {/* Patient Information Header */}
    {this.state.selectedPatient && (
     <Card
      elevation={Elevation.TWO}
      className="bp3-dark"
      style={{ marginBottom: "24px" }}
     >
      <div
       style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr",
        gap: "20px",
       }}
      >
       <div>
        <H4 style={{ color: "var(--text-primary)", margin: "0 0 8px 0" }}>
         {this.state.selectedPatient.name}
        </H4>
        <Text style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
         Patient ID: {this.state.selectedPatient.patientId}
        </Text>
        <br />
        <Text style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
         Age: {this.state.selectedPatient.age} years | Gender:{" "}
         {this.state.selectedPatient.gender}
        </Text>
       </div>
       <div>
        <Text style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
         Phone: {this.state.selectedPatient.phone}
        </Text>
        <br />
        <Text style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
         Address: {this.state.selectedPatient.address}
        </Text>
       </div>
       <div>
        <Tag intent="success" large>
         {this.state.selectedPatient.insuranceStatus} Active
        </Tag>
        <br />
        <Text
         style={{
          color: "var(--text-secondary)",
          fontSize: "14px",
          marginTop: "8px",
         }}
        >
         Approval: Pending
        </Text>
       </div>
      </div>
     </Card>
    )}

    {/* Main Content - 80/20 Layout */}
    <div
     style={{ display: "grid", gridTemplateColumns: "4fr 1fr", gap: "24px" }}
    >
     {/* Left Column - 80% - Workflow */}
     <div>
      {this.renderStepIndicator()}
      {this.renderCurrentStep()}
     </div>

     {/* Right Column - 20% - AI Analysis and Summary */}
     <div>
      {this.renderAIAnalysis()}
      <div style={{ marginTop: "20px" }}>
       {this.renderSurgeryDetailsSummary()}
      </div>
     </div>
    </div>

    {/* Navigation */}
    <div
     style={{
      display: "flex",
      justifyContent: "space-between",
      marginTop: "24px",
      padding: "var(--v3-spacing-lg) 0",
     }}
    >
     <Button
      text="Previous"
      icon={<Icon icon={IconNames.CHEVRON_LEFT} />}
      onClick={this.prevStep}
      disabled={this.state.currentStep === 1}
      outlined
      style={{
       borderColor: "var(--border-color)",
       color: "var(--text-primary)",
      }}
     />

     <div style={{ display: "flex", gap: "12px" }}>
      <Button
       text="Reset"
       icon={<Icon icon={IconNames.REFRESH} />}
       onClick={this.resetWorkflow}
       intent="none"
      />

      {this.state.currentStep < 5 && (
       <Button
        text="Next"
        icon={<Icon icon={IconNames.CHEVRON_RIGHT} />}
        onClick={this.nextStep}
        intent="primary"
        style={{
         backgroundColor: "var(--primary)",
         borderColor: "var(--primary)",
         color: "white",
        }}
       />
      )}
     </div>
    </div>
   </div>
  );
 }
}

export default inject("authStore")(observer(SurgeryWorkflow));
